const FALLBACK_PLAN_LEVELS = {
  free: 0,
  basic: 1,
  full: 2,
  ai: 2,
  friends: 2,
}

export const SUBSCRIPTION_GRACE_PERIOD_DAYS = 20
export const SUBSCRIPTION_GRACE_WINDOW_MS = SUBSCRIPTION_GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000

export function normalizeSubscriptionSnapshot(raw) {
  if (!raw || typeof raw !== 'object') return null

  const aiCreditsBalance = typeof raw?.aiCreditsBalance === 'number'
    ? raw.aiCreditsBalance
    : typeof raw?.ai_credits_balance === 'number'
      ? raw.ai_credits_balance
      : null

  const aiCreditsCap = typeof raw?.aiCreditsCap === 'number'
    ? raw.aiCreditsCap
    : typeof raw?.ai_credits_cap === 'number'
      ? raw.ai_credits_cap
      : null

  return {
    ...raw,
    plan: raw?.plan || 'free',
    status: raw?.status || 'inactive',
    current_period_start: raw?.current_period_start ?? null,
    current_period_end: raw?.current_period_end ?? null,
    cancel_at_period_end: Boolean(raw?.cancel_at_period_end),
    cancel_at: raw?.cancel_at ?? null,
    canceled_at: raw?.canceled_at ?? null,
    ended_at: raw?.ended_at ?? null,
    aiCreditsBalance,
    aiCreditsCap
  }
}

function toDate(value) {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function getSubscriptionAccessEnd(subscription) {
  if (!subscription) return null
  const cancelAt = toDate(subscription?.cancel_at)
  if (subscription?.plan === 'friends' && cancelAt) {
    return cancelAt
  }
  return toDate(subscription?.current_period_end)
}

export function getSubscriptionGraceAccessEnd(subscription) {
  const accessEnd = getSubscriptionAccessEnd(subscription)
  if (!(accessEnd instanceof Date)) return null
  return new Date(accessEnd.getTime() + SUBSCRIPTION_GRACE_WINDOW_MS)
}

export function deriveSubscriptionAccess({subscription, plans, requiredPlan = 'basic'}) {
  const normalized = normalizeSubscriptionSnapshot(subscription)
  if (!normalized) {
    return {
      subscription: null,
      accessEnd: null,
      allowed: false,
      requiredLevel: Number.POSITIVE_INFINITY,
      userLevel: 0
    }
  }

  const accessEnd = getSubscriptionAccessEnd(normalized)
  const planMap = Array.isArray(plans)
    ? plans.reduce((acc, plan) => {
        acc[plan.id] = plan
        return acc
      }, {})
    : {}
  const requiredLevel = planMap[requiredPlan]?.level ?? FALLBACK_PLAN_LEVELS[requiredPlan] ?? Number.POSITIVE_INFINITY
  const userLevel = planMap[normalized.plan]?.level ?? FALLBACK_PLAN_LEVELS[normalized.plan] ?? 0
  const graceAccessEnd = getSubscriptionGraceAccessEnd(normalized)
  const statusHasAccess =
    (normalized.status === 'active' && accessEnd instanceof Date && accessEnd.getTime() > Date.now()) ||
    (normalized.status === 'grace' && graceAccessEnd instanceof Date && graceAccessEnd.getTime() > Date.now())
  const allowed =
    statusHasAccess &&
    userLevel >= requiredLevel

  return {
    subscription: normalized,
    accessEnd,
    graceAccessEnd,
    allowed,
    requiredLevel,
    userLevel
  }
}
